import { useState } from 'react';
import { ThumbsUp, ThumbsDown, MessageSquare, Edit3 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const RatingComponent = ({ onRatingChange, onFeedbackChange, onImprovedTranslationChange, rating, feedback, improvedTranslation }) => {
  const { t } = useTranslation();
  const [sliderValue, setSliderValue] = useState(70); // 0-100 scale, default to 70%

  const getSliderLabel = (value) => {
    if (value >= 90) return 'Excellent';
    if (value >= 70) return 'Very Good';
    if (value >= 50) return 'Good';
    if (value >= 30) return 'Fair';
    if (value >= 10) return 'Poor';
    return 'Very Poor';
  };

  const getSliderColor = (value) => {
    if (value >= 80) return '#22c55e'; // green
    if (value >= 60) return '#84cc16'; // lime
    if (value >= 40) return '#eab308'; // yellow
    if (value >= 20) return '#f97316'; // orange
    return '#ef4444'; // red
  };

  const handleSliderChange = (value) => {
    setSliderValue(value);
    // Send the actual slider value (0-100) instead of converting to 1-5
    onRatingChange(value);
  };

  const handleQuickRating = (value) => {
    setSliderValue(value);
    handleSliderChange(value);
  };

  return (
    <div className="card">
      <h3 className="text-lg font-semibold text-gray-900 mb-6">
        {t('translation.feedback.title')}
      </h3>

      {/* Slider Rating */}
      <div className="mb-6 space-y-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">{t('translation.feedback.qualityScore')}</span>
          <div className="flex items-center gap-2">
            <span 
              className="px-3 py-1 rounded-full text-sm font-semibold text-white"
              style={{ backgroundColor: getSliderColor(sliderValue) }}
            >
              {getSliderLabel(sliderValue)}
            </span>
            <span className="text-sm text-gray-500">({sliderValue}%)</span>
          </div>
        </div>
        
        {/* Custom Slider */}
        <div className="relative">
          <input
            type="range"
            min="0"
            max="100"
            value={sliderValue}
            onChange={(e) => handleSliderChange(parseInt(e.target.value))}
            className="w-full h-2 rounded-lg appearance-none cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/20 slider-thumb"
            style={{
              background: `linear-gradient(to right, 
                #ef4444 0%, 
                #f97316 20%, 
                #eab308 40%, 
                #84cc16 60%, 
                #22c55e 80%, 
                #16a34a 100%
              )`
            }}
          />
        </div>
        
        {/* Quick Rating Buttons */}
        <div className="flex items-center justify-between pt-2">
          <button
            type="button"
            onClick={() => handleQuickRating(15)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <ThumbsDown size={16} />
            {t('translation.feedback.poor')}
          </button>
          <button
            type="button"
            onClick={() => handleQuickRating(85)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg transition-colors"
          >
            <ThumbsUp size={16} />
            {t('translation.feedback.great')}
          </button>
        </div>
      </div>

      {/* Improved Translation Field - shown when rating <= 60% */}
      {sliderValue <= 60 && (
        <div className="space-y-3 pb-4 mb-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Edit3 size={18} className="text-primary-600" />
            <label htmlFor="improved-translation" className="text-sm font-medium text-gray-700">
              {t('translation.feedback.improvedTranslationLabel')}
            </label>
          </div>
          <textarea
            id="improved-translation"
            value={improvedTranslation || ''}
            onChange={(e) => onImprovedTranslationChange(e.target.value)}
            placeholder={t('translation.feedback.improvedTranslationPlaceholder')}
            className="w-full px-4 py-3 border border-orange-300 bg-orange-50 rounded-lg resize-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-colors"
            rows="3"
          />
          <p className="text-xs text-orange-600 flex items-start gap-2">
            <span className="font-semibold">💡 Tip:</span>
            <span>{t('translation.feedback.improvedTranslationTip')}</span>
          </p>
        </div>
      )}

      {/* Feedback Textarea */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <MessageSquare size={18} className="text-gray-500" />
          <label htmlFor="feedback" className="text-sm font-medium text-gray-700">
            {t('translation.feedback.commentsLabel')}
          </label>
        </div>
        <textarea
          id="feedback"
          value={feedback}
          onChange={(e) => onFeedbackChange(e.target.value)}
          placeholder={
            sliderValue < 40 
              ? t('translation.feedback.commentsPlaceholderLowRating')
              : t('translation.feedback.commentsPlaceholder')
          }
          className="w-full px-4 py-3 border border-gray-300 rounded-lg resize-none focus:ring-2 focus:ring-primary focus:border-transparent transition-colors"
          rows="3"
        />
        <p className="text-xs text-gray-500">
          {t('translation.feedback.helpText')}
        </p>
      </div>
    </div>
  );
};

export default RatingComponent;